import { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from './button';
import { Input } from './input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './command';
import { Check, ChevronsUpDown, Plus, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SearchableDescriptionSelect - Combobox for line item descriptions.
 * Lets user pick from predefined descriptions or type a custom one.
 *
 * @param {string} value - Current description text
 * @param {function} onValueChange - Callback when description changes
 * @param {string} workspaceId - Current workspace ID
 * @param {Array} descriptions - List of predefined description objects [{description_id, text, category}]
 * @param {function} onDescriptionCreated - Callback when a new description is saved
 * @param {string} placeholder - Placeholder text
 * @param {string} className - Additional CSS classes
 * @param {string} testId - data-testid attribute
 */
export function SearchableDescriptionSelect({
  value = '',
  onValueChange,
  workspaceId,
  descriptions = [],
  onDescriptionCreated,
  placeholder = 'Item description',
  className,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  // Filter descriptions based on search
  const filtered = useMemo(() => {
    if (!search) return descriptions;
    const lower = search.toLowerCase();
    return descriptions.filter((d) => d.text?.toLowerCase().includes(lower));
  }, [descriptions, search]);

  // Check if current search text matches an existing description exactly
  const exactMatch = useMemo(() => {
    if (!search) return false;
    return descriptions.some(
      (d) => d.text?.toLowerCase() === search.toLowerCase()
    );
  }, [descriptions, search]);

  // Save a new predefined description
  const handleSaveNew = async (text) => {
    if (!text?.trim() || !workspaceId) return;
    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${workspaceId}/predefined-descriptions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: text.trim(), category: 'general' }),
        }
      );
      if (response.ok) {
        const created = await response.json();
        if (!created.already_exists) {
          toast.success('Description saved to presets');
        }
        if (onDescriptionCreated) {
          onDescriptionCreated(created);
        }
        onValueChange(created.text);
        setOpen(false);
        setSearch('');
      }
    } catch {
      toast.error('Failed to save description');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between font-normal text-left',
            !value && 'text-muted-foreground',
            className
          )}
          data-testid={testId}
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search or type new..."
            value={search}
            onValueChange={setSearch}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandList>
            {/* Custom text option - always available when typing */}
            {search && !exactMatch && (
              <CommandGroup>
                <CommandItem
                  onSelect={() => {
                    onValueChange(search);
                    setOpen(false);
                    setSearch('');
                  }}
                  className="cursor-pointer"
                >
                  <span className="mr-2 text-xs text-muted-foreground">Use:</span>
                  <span className="font-medium truncate">{search}</span>
                </CommandItem>
                <CommandItem
                  onSelect={() => handleSaveNew(search)}
                  className="text-emerald-600 dark:text-emerald-400 cursor-pointer"
                  disabled={saving}
                >
                  {saving ? (
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Plus className="mr-2 h-3.5 w-3.5" />
                  )}
                  Save &amp; use &quot;{search}&quot;
                </CommandItem>
              </CommandGroup>
            )}

            {search && !exactMatch && descriptions.length > 0 && (
              <CommandSeparator />
            )}

            {/* Predefined descriptions list */}
            {descriptions.length > 0 && (
              <CommandGroup heading="Saved Descriptions">
                {filtered.length === 0 ? (
                  <CommandEmpty>No matches found.</CommandEmpty>
                ) : (
                  filtered.map((desc) => (
                    <CommandItem
                      key={desc.description_id}
                      value={desc.description_id}
                      onSelect={() => {
                        onValueChange(desc.text);
                        setOpen(false);
                        setSearch('');
                      }}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn(
                          'mr-2 h-3.5 w-3.5',
                          value === desc.text ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <span className="truncate">{desc.text}</span>
                    </CommandItem>
                  ))
                )}
              </CommandGroup>
            )}

            {/* Empty state when no descriptions at all */}
            {descriptions.length === 0 && !search && (
              <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                <p>No saved descriptions yet.</p>
                <p className="text-xs mt-1">Type to add your first one.</p>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default SearchableDescriptionSelect;
